<template>
  <div>
    <div class="label">Shift handover comment</div>
    <div>{{ shiftOverview.comment || 'No comment was provided' }}</div>

    <div class="mt-2">
      <form v-on:submit.prevent="submitSearch">
        <div class="shift-hd-details">
          <div class="label mr-3">Board Items</div>

          <div>
            <checkbox-with-label
              :label="'Relevant items only'"
              v-model="onlyRelevantItems"
              @input="(value) => (isDirty = true)"
            ></checkbox-with-label>
          </div>

          <div class="search-items">
            <div class="input-group">
              <input v-debounce="submitSearch" type="text" class="form-control input-sm" placeholder="Search..." v-model="search" />
              <div class="input-group-append" @click="submitSearch">
                <i class="fa fa-search input-group-text"></i>
              </div>
            </div>
          </div>
        </div>
      </form>

      <table class="table table-striped fixed-header">
        <thead>
          <tr>
            <th scope="col">Category & Type</th>
            <th scope="col">Title & Description</th>
            <th scope="col">Labels</th>
            <th scope="col">Attachments</th>
            <th scope="col">Comments</th>
            <th scope="col">Subtasks</th>
            <th scope="col">Activity Date</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          <shift-board-item-overview
            :item="item"
            v-for="(item, index) in items"
            :key="index"
            :action="navigateToBoardItem"
            :action-param="item.boardItemId"
            :display-relevant-badge="item.reportRelevant"
          ></shift-board-item-overview>
        </tbody>
      </table>

      <div v-if="!loading && (!items || !items.length)">
        <div class="label justify-content-center d-flex">No Board items found</div>
      </div>

      <div v-if="loading" class="d-flex align-items-center justify-content-center p-5">
        <loading-spinner></loading-spinner>
      </div>
    </div>

    <router-view></router-view>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { Prop } from 'vue-property-decorator';
  import { ShiftHandoverOverviewDto, ShiftOverviewRowDto } from "@/apiclient/apiclient_generated";
  import { OverviewModule, OverviewStore } from '@/store/overview';
  import ShiftOverviewRequestParams from '@/types/requestParams/shiftOverviewRequestParams';
  import ShiftBoardItemOverview from '@/components/Overview/ShiftBoardItemOverview.vue';
  import LoadingSpinner from '@/components/Misc/LoadingSpinner.vue';
  import CheckboxWithLabel from '@/components/Misc/CheckboxWithLabel.vue';
  import { debounce } from 'vue-debounce';

  @Component({
    components: {
      ShiftBoardItemOverview,
      LoadingSpinner,
      'checkbox-with-label': CheckboxWithLabel,
    },
  })
  export default class ShiftOverview extends Vue {
    private overviewStore: OverviewModule = OverviewStore;
    private search = '';
    private loading = false;
    private onlyRelevantItems = false;

    @Prop()
    shiftOverview: ShiftHandoverOverviewDto;

    async created() {
      this.submitSearch(true);
    }

    private get items(): ShiftOverviewRowDto[] {
      return this.overviewStore
        .rowsFor(this.shiftOverview.id)
        .filter((_) => (this.onlyRelevantItems ? _.reportRelevant : true));
    }

    get requestParams(): ShiftOverviewRequestParams {
      return {
        entityId: this.shiftOverview.id,
        searchString: this.search,
      } as ShiftOverviewRequestParams;
    }

    async submitSearch(immediate = false) {
      const wait = immediate ? '0ms' : '1000ms';
      debounce(async () => {
        this.loading = true;
        await this.overviewStore.loadShiftOverviewRows(this.requestParams);
        this.loading = false;
      }, wait)();
    }

    navigateToBoardItem(event) {
      this.$router.push({
        name: 'shiftBoardItemOverview',
        params: { shiftBoardItemOverviewId: event },
      });
    }
  }
</script>

<style scoped lang="scss">
  .shift-hd-details {
    display: flex;
    align-items: center;
    margin-bottom: 10px;

    .label {
      margin-bottom: 0 !important;
    }

    .search-items {
      margin-left: auto;

      i {
        padding-top: 10px;
      }
      i:hover {
        cursor: pointer;
      }
    }
  }
</style>
